import { useEffect, useRef, useState } from 'react';

/**
 * True once the element has been scrolled near the viewport, and true forever
 * after.
 *
 * Used to defer the dashboard's lower panels. Once is deliberate: a query that
 * un-enabled itself when the panel scrolled away would refire on the way back,
 * which is the opposite of the saving.
 *
 * The 200px margin means the request starts slightly before the panel is
 * visible, so the data usually arrives with it rather than after it.
 *
 * Falls back to "in view" where `IntersectionObserver` is unavailable. Deferring
 * is an optimisation; failing to load the panel at all is not an acceptable way
 * to lose it.
 */
export function useInViewOnce<T extends HTMLElement>(rootMargin = '200px') {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return [ref, inView] as const;
}
